import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  StatusBar,
  ScrollView,
  Image,
  ActivityIndicator,
  Platform,
} from 'react-native';
import type { V2DepositAccount, V2HistoryRecord, V2TransactionType } from '../../types/v2';
import SimpleHeader from '../../components/SimpleHeader';
import TabBar from '../../components/TabBar';
import { COLORS } from '../../theme/themeTokens';
import {
  v2GetAccountDepositView,
  v2GetMyHistory,
  v2GetTransactionTypes,
  v2TransactionTypeIconUrl,
} from '../../services/v2/api';
import { useAuth } from '../../contexts/AuthContext';
import TokenIcon from '../../components/UIComponents/TokenIcon';
import { formatSignedAmount, signedAmountStyle } from '../../utils/transactionSign';
import { isConfirmedStatus } from '../../utils/transactionStatus';

// ── helpers ──────────────────────────────────────────────────────────────────
//
// InvestmentAccountScreen — детальный экран для T-Fund-like accounts
// (account_type_code = 'investment'). Стрипованный вариант
// DepositAccountScreen'а: hero + history без APY/term/accrued/action row.
// Классический term-deposit (account_type_code='deposit') рендерится через
// DepositAccountScreen. Splitting живёт в App.tsx handler'e.
//
// Backend endpoint v2GetAccountDepositView принимает оба типа через
// IsDepositLikeAccountTypeCode; APY/term просто игнорируем в DTO'шке.

/** "DD.MM.YY" — mirror of DepositAccountScreen formatting; используется в
 *  Term range секции hero-card'а. */
const formatShortDate = (dateString: string | null | undefined): string => {
  if (!dateString) return '';
  const d = new Date(dateString);
  const day = d.getDate().toString().padStart(2, '0');
  const month = (d.getMonth() + 1).toString().padStart(2, '0');
  const year = d.getFullYear().toString().slice(-2);
  return `${day}.${month}.${year}`;
};

const formatBigAmount = (amount: string): { whole: string; fraction: string } => {
  const num = parseFloat(amount);
  if (isNaN(num)) return { whole: '0', fraction: '00' };
  const parts = num.toFixed(2).split('.');
  const int = parts[0];
  const dec = parts[1] ?? '00';
  return {
    whole: int.replace(/\B(?=(\d{3})+(?!\d))/g, ' '),
    fraction: dec,
  };
};

const formatFullDate = (dateString: string): string => {
  const d = new Date(dateString);
  const day = d.getDate().toString().padStart(2, '0');
  const month = (d.getMonth() + 1).toString().padStart(2, '0');
  const year = d.getFullYear();
  return `${day}.${month}.${year}`;
};

const groupByDate = (
  items: V2HistoryRecord[],
): { date: string; items: V2HistoryRecord[] }[] => {
  const sorted = [...items].sort((a, b) => {
    const dateDiff = new Date(b.date).getTime() - new Date(a.date).getTime();
    if (dateDiff !== 0) return dateDiff;
    return (b.sort_order ?? 0) - (a.sort_order ?? 0);
  });
  const map = new Map<string, V2HistoryRecord[]>();
  for (const rec of sorted) {
    const key = formatFullDate(rec.date);
    const group = map.get(key);
    if (group) group.push(rec);
    else map.set(key, [rec]);
  }
  return Array.from(map.entries()).map(([date, items]) => ({ date, items }));
};

const capitalize = (s: string): string =>
  s ? s.charAt(0).toUpperCase() + s.slice(1) : s;

const normalizeTypeName = (name: string | null | undefined): string => {
  const lower = (name || '').toLowerCase().trim();
  if (lower.includes('withdr')) return 'withdraw';
  if (lower.includes('depos') || lower.includes('inflow')) return 'deposit';
  return lower;
};

// ── Transaction Item ─────────────────────────────────────────────────────────

const TransactionItem: React.FC<{
  item: V2HistoryRecord;
  typeIconUri: string | null;
  amountSign: V2TransactionType['amount_sign'] | null;
}> = ({ item, typeIconUri, amountSign }) => {
  const formattedAmount = formatSignedAmount(item.amount, amountSign);
  const { color: amountColor } = signedAmountStyle(amountSign);

  return (
    <View style={styles.txRow}>
      <View style={styles.txIconWrap}>
        {typeIconUri ? (
          <Image source={{ uri: typeIconUri }} style={styles.txIcon} resizeMode="contain" />
        ) : (
          <View style={[styles.txIcon, styles.txIconPlaceholder]}>
            <Text style={styles.txIconPlaceholderText}>
              {capitalize(item.transaction_type_name).charAt(0) || '?'}
            </Text>
          </View>
        )}
      </View>
      <Text style={styles.txTypeName} numberOfLines={1}>
        {capitalize(item.transaction_type_name)}
      </Text>
      <Text style={[styles.txAmount, { color: amountColor }]} numberOfLines={1}>
        {formattedAmount}{' '}
        <Text style={styles.txCurrency}>{item.currency}</Text>
      </Text>
    </View>
  );
};

// ── Screen Props ─────────────────────────────────────────────────────────────

interface Props {
  /** Investment (T-Fund-like) account id. */
  accountId: string;
  onBack: () => void;
  onNavigate: (screen: string) => void;
}

// ── Screen ───────────────────────────────────────────────────────────────────

const InvestmentAccountScreen: React.FC<Props> = ({ accountId, onBack, onNavigate }) => {
  const { isAuthenticated } = useAuth();
  const statusBarHeight = Platform.OS === 'android' ? 28 : 0;

  const [account, setAccount] = useState<V2DepositAccount | null>(null);
  const [accountLoading, setAccountLoading] = useState(true);
  const [accountError, setAccountError] = useState<string | null>(null);

  const [history, setHistory] = useState<V2HistoryRecord[]>([]);
  const [historyLoading, setHistoryLoading] = useState(true);

  const [transactionTypes, setTransactionTypes] = useState<V2TransactionType[]>([]);

  const signByExactName = useMemo(() => {
    const m = new Map<string, V2TransactionType['amount_sign']>();
    for (const t of transactionTypes) m.set(t.name, t.amount_sign);
    return m;
  }, [transactionTypes]);
  const iconByExactName = useMemo(() => {
    const m = new Map<string, string | null>();
    for (const t of transactionTypes) m.set(t.name, v2TransactionTypeIconUrl(t));
    return m;
  }, [transactionTypes]);
  const iconByCategory = useMemo(() => {
    const m = new Map<string, string | null>();
    for (const t of transactionTypes) {
      const key = normalizeTypeName(t.name);
      const iconUri = v2TransactionTypeIconUrl(t);
      if (!m.has(key) || (m.get(key) == null && iconUri != null)) {
        m.set(key, iconUri);
      }
    }
    return m;
  }, [transactionTypes]);

  const fetchAccount = useCallback(async () => {
    if (!isAuthenticated) return;
    setAccountError(null);
    try {
      const data = await v2GetAccountDepositView(accountId);
      setAccount(data);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to load investment account';
      setAccountError(msg);
    } finally {
      setAccountLoading(false);
    }
  }, [isAuthenticated, accountId]);

  const fetchHistory = useCallback(async (balanceID: string) => {
    setHistoryLoading(true);
    try {
      const res = await v2GetMyHistory({ balance_id: balanceID });
      const confirmed = (res.records ?? []).filter((r) =>
        isConfirmedStatus(r.transaction_status_name),
      );
      setHistory(confirmed);
    } catch {
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  const fetchTypes = useCallback(async () => {
    try {
      const res = await v2GetTransactionTypes();
      setTransactionTypes(res.transaction_types ?? []);
    } catch {
      setTransactionTypes([]);
    }
  }, []);

  useEffect(() => {
    fetchAccount();
    fetchTypes();
  }, [fetchAccount, fetchTypes]);

  useEffect(() => {
    if (account?.investment_balance_id) {
      fetchHistory(account.investment_balance_id);
    } else {
      setHistoryLoading(false);
    }
  }, [account?.investment_balance_id, fetchHistory]);

  const currency = account?.symbol || account?.currency || '';
  // Hero-name — то же что на dashboard tile'е: account.name если admin ввёл
  // (backend вернул это поле через DepositAccountDTO.Name после правки —
  // см. deposit_account_dto.go). Fallback на currency для legacy аккаунтов
  // без name. Investment accounts не имеют APY/term метаданных, поэтому
  // formatDepositMeta не используем.
  const heroName = account?.name || currency || '—';
  const balance = formatBigAmount(account?.current_balance ?? '0');
  const accrued = formatSignedAmount(account?.accrued_interest ?? '0', 'positive');
  // Term показываем только если обе даты присутствуют — при частичных
  // данных колонку полностью скрываем (не рисуем label + placeholder).
  const hasTerm = !!account?.deposit_date_from && !!account?.deposit_date_to;
  const termRange = hasTerm
    ? `${formatShortDate(account!.deposit_date_from)} – ${formatShortDate(account!.deposit_date_to)}`
    : null;

  return (
    <View style={styles.root}>
      <StatusBar barStyle="light-content" backgroundColor={COLORS.primaryNavy} />

      <View style={{
        paddingTop: statusBarHeight,
        paddingBottom: Platform.OS === 'android' ? 12 : 0,
        backgroundColor: COLORS.primaryNavy,
      }}>
        <SimpleHeader
          title="Investments"
          onBack={onBack}
          backgroundVariant="primaryNavy"
        />
      </View>

      {accountLoading ? (
        <View style={styles.topCardLoading}>
          <ActivityIndicator size="small" color={COLORS.white} />
        </View>
      ) : accountError ? (
        <View style={styles.topCardError}>
          <Text style={styles.errorText}>{accountError}</Text>
        </View>
      ) : (
        <View style={styles.topCard}>
          <View style={styles.metaRow}>
            <TokenIcon
              symbol={currency}
              iconUrl={account?.icon_url ?? null}
              style={styles.tokenIcon}/>
            <Text style={styles.metaText}>{heroName}</Text>
          </View>

          <View style={styles.balanceRow}>
            <Text style={styles.balanceWhole}>{balance.whole}</Text>
            <Text style={styles.balanceFraction}>,{balance.fraction}</Text>
            <Text style={styles.balanceCurrency}> {currency}</Text>
          </View>

          {/* Split row: Accrued interest всегда виден; Term показываем
              только когда обе даты присутствуют на investment_account. */}
          <View style={styles.splitRow}>
            <View style={styles.splitCol}>
              <Text style={styles.splitLabel}>Accrued interest</Text>
              <Text style={styles.splitValueAccrued}>{accrued} {currency}</Text>
            </View>
            {hasTerm && (
              <View style={styles.splitCol}>
                <Text style={styles.splitLabel}>Term</Text>
                <Text style={styles.splitValue}>{termRange}</Text>
              </View>
            )}
          </View>
        </View>
      )}

      {/* Gap между hero и history sheet'ом — 20 dp на navy stripe'e.
          Заменяет action row, которого здесь нет. */}
      <View style={styles.heroToHistoryGap} />

      <ScrollView
        style={styles.historyScroll}
        contentContainerStyle={styles.historyContent}
        showsVerticalScrollIndicator={false}
      >
        <Text style={styles.historyTitle}>History</Text>
        {historyLoading ? (
          <View style={styles.loadingRow}>
            <ActivityIndicator size="small" color={COLORS.primaryNavy} />
          </View>
        ) : history.length === 0 ? (
          <View style={styles.emptyRow}>
            <Text style={styles.emptyText}>No transactions yet</Text>
          </View>
        ) : (
          groupByDate(history).map((group) => (
            <View key={group.date}>
              <Text style={styles.dateHeader}>{group.date}</Text>
              {group.items.map((item) => {
                const name = item.transaction_type_name;
                const amountSign = signByExactName.get(name) ?? null;
                const iconUri =
                  iconByExactName.get(name) ??
                  iconByCategory.get(normalizeTypeName(name)) ??
                  null;
                return (
                  <TransactionItem
                    key={item.id}
                    item={item}
                    typeIconUri={iconUri}
                    amountSign={amountSign}
                  />
                );
              })}
            </View>
          ))
        )}
      </ScrollView>

      <TabBar activeTab="dashboard" onNavigate={onNavigate as never} />
    </View>
  );
};

export default InvestmentAccountScreen;

// ── Styles ───────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: COLORS.primaryNavy,
  },
  topCardLoading: {
    paddingVertical: 24,
    alignItems: 'center',
  },
  topCardError: {
    paddingHorizontal: 20,
    paddingVertical: 16,
  },
  errorText: {
    fontFamily: 'Inter',
    fontSize: 14,
    color: COLORS.white,
    opacity: 0.8,
    textAlign: 'center',
  },
  topCard: {
    marginHorizontal: 16,
    marginTop: 0,
    paddingHorizontal: 16,
    paddingVertical: 16,
    borderRadius: 12,
    backgroundColor: 'rgba(255,255,255,0.04)',
  },
  metaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 20,
  },
  tokenIcon: {
    width: 28,
    height: 28,
    marginRight: 10,
  },
  metaText: {
    fontFamily: 'Inter',
    fontSize: 14,
    fontWeight: '500',
    color: COLORS.white,
  },
  balanceRow: {
    flexDirection: 'row',
    alignItems: 'baseline',
    // 20 dp gap до split-row (Accrued / Term) — совпадает с ритмом
    // DepositAccountScreen.
    marginBottom: 20,
  },
  // Helvetica Neue per design (Title1/Semibold + Title1/Regular + Title3/Regular).
  // iOS ships HelveticaNeue natively; Android fallback на sans-serif —
  // визуально ближайшее из системных, чтобы не тянуть ttf в bundle.
  balanceWhole: {
    fontFamily: Platform.OS === 'ios' ? 'HelveticaNeue' : 'sans-serif',
    fontSize: 32,
    fontWeight: '500',
    lineHeight: 48,
    color: COLORS.white,
    textAlign: 'center',
  },
  balanceFraction: {
    fontFamily: Platform.OS === 'ios' ? 'HelveticaNeue' : 'sans-serif',
    fontSize: 32,
    fontWeight: '400',
    lineHeight: 48,
    color: COLORS.white,
  },
  balanceCurrency: {
    fontFamily: Platform.OS === 'ios' ? 'HelveticaNeue' : 'sans-serif',
    fontSize: 20,
    fontWeight: '400',
    lineHeight: 32,
    color: COLORS.blueSteel,
    marginLeft: 4,
  },
  // Accrued / Term split row — 1-в-1 стили с DepositAccountScreen.
  splitRow: {
    flexDirection: 'row',
  },
  splitCol: {
    flex: 1,
  },
  splitLabel: {
    fontFamily: 'Inter',
    fontSize: 13,
    color: COLORS.white,
    opacity: 0.7,
    marginBottom: 4,
  },
  splitValue: {
    fontFamily: 'Inter',
    fontSize: 14,
    fontWeight: '500',
    color: COLORS.white,
  },
  splitValueAccrued: {
    fontFamily: 'Inter',
    fontSize: 14,
    fontWeight: '500',
    color: '#5EC07E',
  },
  heroToHistoryGap: {
    height: 20,
    backgroundColor: COLORS.primaryNavy,
  },
  historyScroll: {
    flex: 1,
    backgroundColor: COLORS.pureLight,
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
    overflow: 'hidden',
  },
  historyContent: {
    paddingTop: 20,
    paddingHorizontal: 20,
    paddingBottom: 140,
  },
  historyTitle: {
    fontFamily: 'Inter',
    fontSize: 20,
    fontWeight: '600',
    color: COLORS.primaryNavy,
    marginBottom: 20,
  },
  loadingRow: {
    paddingVertical: 16,
    alignItems: 'center',
  },
  emptyRow: {
    paddingVertical: 16,
    alignItems: 'center',
  },
  emptyText: {
    fontFamily: 'Inter',
    fontSize: 14,
    color: COLORS.blueSteel,
  },
  dateHeader: {
    fontFamily: 'Inter',
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 20,
    color: COLORS.blueSteel,
    marginTop: 16,
    marginBottom: 8,
  },
  txRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 10,
  },
  txIconWrap: {
    width: 36,
    height: 36,
    marginRight: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
  txIcon: {
    width: 36,
    height: 36,
  },
  txIconPlaceholder: {
    backgroundColor: COLORS.softGrey,
    borderRadius: 8,
  },
  txIconPlaceholderText: {
    fontFamily: 'Inter',
    fontSize: 18,
    fontWeight: '500',
    color: COLORS.blueSteel,
  },
  txTypeName: {
    flex: 1,
    fontFamily: 'Inter',
    fontSize: 16,
    fontWeight: '400',
    lineHeight: 20,
    color: COLORS.primaryNavy,
  },
  txAmount: {
    fontFamily: 'Inter',
    fontSize: 16,
    fontWeight: '400',
    lineHeight: 20,
  },
  txCurrency: {
    fontFamily: 'Inter',
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 20,
  },
});
